package com.ijoin.data.api

import com.ijoin.data.api.ApiClient.addAuthToken
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.http.*
import kotlinx.serialization.Serializable

class AiApi {
    private val client = ApiClient.client
    private val baseUrl = "${ApiClient.getBaseUrl()}/api/ai"

    /**
     * Transcribe audio data to text.
     *
     * @param audioData raw audio bytes
     * @param filename original filename (used for format detection)
     * @param language optional language code (e.g., "ru", "en")
     * @return transcribed text
     */
    suspend fun transcribeAudio(
        audioData: ByteArray,
        filename: String = "audio.webm",
        language: String? = null
    ): Result<TranscriptionResponse> {
        return safeApiCall {
            client.submitFormWithBinaryData(
                url = "$baseUrl/transcribe",
                formData = formData {
                    append("file", audioData, Headers.build {
                        append(HttpHeaders.ContentType, getMimeType(filename))
                        append(HttpHeaders.ContentDisposition, "filename=\"$filename\"")
                    })
                    if (language != null) {
                        append("language", language)
                    }
                }
            ) {
                addAuthToken()
            }
        }.mapCatching { response -> response.body() }
    }

    /**
     * Generate an event description from the provided title.
     */
    suspend fun makeDescription(title: String): Result<MakeDescriptionResponse> {
        val request = MakeDescriptionRequest(title = title)
        return safeApiCall {
            client.post("$baseUrl/make-description") {
                addAuthToken()
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        }.mapCatching { response -> response.body() }
    }

    /**
     * Format and finalize a draft event description.
     */
    suspend fun formatDescription(draft: String): Result<FormatDescriptionResponse> {
        val request = FormatDescriptionRequest(draft = draft)
        return safeApiCall {
            client.post("$baseUrl/format-description") {
                addAuthToken()
                contentType(ContentType.Application.Json)
                setBody(request)
            }
        }.mapCatching { response -> response.body() }
    }

    private fun getMimeType(filename: String): String {
        return when {
            filename.endsWith(".webm", ignoreCase = true) -> "audio/webm"
            filename.endsWith(".mp3", ignoreCase = true) -> "audio/mpeg"
            filename.endsWith(".m4a", ignoreCase = true) -> "audio/mp4"
            filename.endsWith(".wav", ignoreCase = true) -> "audio/wav"
            filename.endsWith(".ogg", ignoreCase = true) -> "audio/ogg"
            else -> "audio/webm"
        }
    }
}

@Serializable
data class TranscriptionResponse(
    val text: String,
    val language: String? = null,
    val provider: String? = null
)

@Serializable
data class MakeDescriptionRequest(
    val title: String
)

@Serializable
data class MakeDescriptionResponse(
    val description: String,
    val provider: String? = null
)

@Serializable
data class FormatDescriptionRequest(
    val draft: String
)

@Serializable
data class FormatDescriptionResponse(
    val description: String,
    val provider: String? = null
)
